import { LogManager } from '@src/utility/Logger';

import { sameOriginalIndices } from '../../models/karaoke/CaptionLineBreak';
import { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import { mergePhraseSoftBreaks } from '../../services/captionSoftBreaks';
import { joinCaptionRowTexts } from '../../services/karaokeSegmentation';
import { AbstractCommand } from '../AbstractCommand';

import type { CaptionLineBreak } from '../../models/karaoke/CaptionLineBreak';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:syntheticBreak');

/**
 * PLAT-9623: merge two adjacent caption clips back into one. Used to
 * remove ANY caption-row boundary the user no longer wants — both
 * boundaries we ourselves introduced (synthetic break inside a
 * phrase) AND natural producer boundaries (different source phrases
 * segmented apart by `segmentTokensByPhrase`).
 *
 * Two cases:
 *
 *  - Shared token arrays (clips came from a previous split): combine
 *    translatedPhrases / translatedText, fuse two halves of the same
 *    source phrase if `leftLast.endTs ≈ rightFirst.ts`, drop the
 *    matching `CaptionLineBreak` record so re-produce doesn't
 *    re-introduce the split.
 *  - Distinct token arrays (natural producer boundary): concat the
 *    token arrays, concat translatedPhrases / translatedText. The
 *    merge produces a multi-phrase clip. Note: this won't survive a
 *    re-produce — the producer's segmentation will split them apart
 *    again. That's an acceptable MVP limitation; persistence is a
 *    follow-up.
 *
 * PLAT-10945: one boundary is NOT removable — a boundary where the two
 * rows belong to different speakers. Joining those would mix two
 * people's words into one caption row. See `captionClipsCrossSpeakers`.
 */
/**
 * PLAT-10945: the distinct speaker labels a caption row's tokens carry.
 *
 * ONLY labels that are set are collected. `speaker_label` is optional: whitespace
 * never carries one, and some providers leave marks unlabelled too (on one
 * measured story 873 of 1,659 source elements carry a label and 786 carry
 * nothing). Counting an absent label as a value would make an unlabelled row
 * read as a second speaker and refuse a legitimate join.
 *
 * Punctuation usually DOES carry a label (another measured story labels 1,078 of
 * 1,078 marks). Until PLAT-11670 section reassignment relabelled words only, so a
 * reassigned row held the old speaker's label on its commas and this check
 * refused a same-speaker join. The writers now relabel marks with their words
 * (`@src/utility/speakerRelabel.ts`), so a row's labels agree again — but only
 * for a passage written since PLAT-11670. Repair rides on a user action over the
 * passage; there is no repair on load and no migration, so a row reassigned
 * before PLAT-11670 still holds the old shape and this check still refuses it.
 */
function definedSpeakerLabels(clip: KaraokeClip): Set<string> {
  // PLAT-11905 — a dub-timed caption has no source words; its speakers are
  // those of the dub lines its words come from.
  if (clip.dubTiming) return new Set(clip.dubTiming.speakers);
  const labels = new Set<string>();
  for (const token of clip.transcriptTokens) {
    if (token.speaker_label) labels.add(token.speaker_label);
  }
  return labels;
}

/**
 * PLAT-10945: would joining these two caption rows put two people's words into
 * one row?
 *
 * True only when BOTH rows carry at least one speaker label AND the two sets of
 * labels differ. A row carrying no label at all is not a second speaker, so it
 * never blocks a join.
 *
 * Note this can only ever be true across a natural producer boundary. Two rows
 * that came from a previous user split share ONE token array by reference, so
 * their label sets are the same set and always match — undoing your own split
 * is never refused.
 */
export function captionClipsCrossSpeakers(
  leftClip: KaraokeClip,
  rightClip: KaraokeClip,
): boolean {
  const left = definedSpeakerLabels(leftClip);
  const right = definedSpeakerLabels(rightClip);
  // PLAT-11905 — joining two dub-timed captions also takes in the rows the
  // clip hides between them, so their speakers are joined too.
  for (const speaker of rightClip.dubTiming?.hiddenSpeakersBefore ?? []) {
    right.add(speaker);
  }
  if (left.size === 0 || right.size === 0) return false;
  if (left.size !== right.size) return true;
  for (const label of left) {
    if (!right.has(label)) return true;
  }
  return false;
}

/**
 * PLAT-10945: read-only — would joining the row with this id into the row above
 * it be refused for crossing a speaker change? Mutates nothing.
 *
 * Exported so the captions view can say WHY a Backspace refused without
 * restating the rule: `execute()` below and this function share the one
 * definition in `captionClipsCrossSpeakers`.
 */
export function captionMergeWouldCrossSpeakers(
  clips: KaraokeClip[],
  rightClipId: string,
): boolean {
  const rightIdx = clips.findIndex(c => c.id === rightClipId);
  if (rightIdx <= 0) return false;
  return captionClipsCrossSpeakers(clips[rightIdx - 1], clips[rightIdx]);
}

export class MergeAdjacentCaptionClipsCommand extends AbstractCommand {
  private rightClipId: string;

  private prevClips: KaraokeClip[] | null = null;
  private prevBreaks: CaptionLineBreak[] | null = null;

  constructor(editor: VideoEditor, rightClipId: string) {
    super(editor);
    this.rightClipId = rightClipId;
  }

  getName(): string {
    return 'MergeAdjacentCaptionClipsCommand';
  }

  getDescription(): string {
    return 'Merge adjacent caption clips';
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) {
      logger.warn('execute: no karaoke track');
      return false;
    }

    const rightIdx = track.karaokeClips.findIndex(
      c => c.id === this.rightClipId,
    );
    if (rightIdx <= 0) {
      logger.warn('execute: right clip not found or no left neighbor', {
        rightClipId: this.rightClipId,
      });
      return false;
    }
    const rightClip = track.karaokeClips[rightIdx];
    const leftClip = track.karaokeClips[rightIdx - 1];

    // PLAT-10945: refuse a join that would put two speakers' words in one
    // caption row. Before any mutation and before the undo snapshot, so this
    // is the same clean refusal as the two above. Such a row is wrong for
    // dubbing (the speaker chooses the voice) and, with the span editor's
    // speaker guard in place, could then only be corrected one speaker's half
    // at a time — a row the application would not let the user fix as a unit.
    if (captionClipsCrossSpeakers(leftClip, rightClip)) {
      logger.warn('execute: refusing a merge across a speaker change', {
        rightClipId: this.rightClipId,
      });
      return false;
    }

    // Two regimes:
    //   sharedTokens === true  → previously split (single source token
    //                            array, just a phrase boundary or
    //                            in-phrase synthetic split). Reuse the
    //                            shared token array, fuse phrase
    //                            halves, drop break record.
    //   sharedTokens === false → natural producer boundary. Concat
    //                            the two token arrays into one and
    //                            concat phrases verbatim.
    const sharedTokens =
      leftClip.transcriptTokens === rightClip.transcriptTokens;

    // Capture undo state.
    this.prevClips = [...track.karaokeClips];
    this.prevBreaks = [...track.syntheticBreaks];

    const leftPhrases = leftClip.translatedPhrases ?? [];
    const rightPhrases = rightClip.translatedPhrases ?? [];

    // Phrase fusion only applies in the shared-tokens regime, where
    // left's last and right's first might be two halves of the same
    // source phrase produced by an in-phrase synthetic split. Across
    // a natural boundary (distinct tokens) the phrases come from
    // different source phrases — concat verbatim.
    let mergedPhrases: {
      text: string;
      ts: number;
      endTs: number;
      originalIndices?: number[];
    }[];
    let inPhraseFusion = false;
    // PLAT-11914: where the right clip's first phrase starts inside the
    // fused phrase, to rebase its soft line breaks.
    let rightPhraseShift = 0;
    if (
      sharedTokens &&
      leftPhrases.length > 0 &&
      rightPhrases.length > 0 &&
      Math.abs(leftPhrases[leftPhrases.length - 1].endTs - rightPhrases[0].ts) <
        1e-6
    ) {
      const lastLeft = leftPhrases[leftPhrases.length - 1];
      const firstRight = rightPhrases[0];
      const fusedText = joinCaptionRowTexts([lastLeft.text, firstRight.text]);
      rightPhraseShift = fusedText.length - firstRight.text.length;
      mergedPhrases = [
        ...leftPhrases.slice(0, -1),
        {
          text: fusedText,
          ts: lastLeft.ts,
          endTs: firstRight.endTs,
          originalIndices:
            lastLeft.originalIndices ?? firstRight.originalIndices,
        },
        ...rightPhrases.slice(1),
      ];
      inPhraseFusion = true;
    } else {
      mergedPhrases = [...leftPhrases, ...rightPhrases];
    }

    const mergedText =
      leftClip.translatedText && rightClip.translatedText
        ? // CJK-aware join (PLAT-11468): no ASCII space at full-width joins.
          joinCaptionRowTexts([
            leftClip.translatedText,
            rightClip.translatedText,
          ])
        : (leftClip.translatedText ?? rightClip.translatedText);

    const mergedHidden = Array.from(
      new Set([
        ...leftClip.hiddenTranscriptTokenIds,
        ...rightClip.hiddenTranscriptTokenIds,
      ]),
    );
    const mergedIgnored = Array.from(
      new Set([
        ...leftClip.ignoredByFiltersIds,
        ...rightClip.ignoredByFiltersIds,
      ]),
    );

    // If both halves carried matching phrase identity AND the merge
    // collapses back to one phrase, restore the identity on the merged
    // clip so future synthetic-break records can target it again.
    const sharedIdentity =
      leftClip.phraseOriginalIndices &&
      rightClip.phraseOriginalIndices &&
      sameOriginalIndices(
        leftClip.phraseOriginalIndices,
        rightClip.phraseOriginalIndices,
      )
        ? leftClip.phraseOriginalIndices
        : leftClip.phraseOriginalIndices && !rightClip.phraseOriginalIndices
          ? leftClip.phraseOriginalIndices
          : !leftClip.phraseOriginalIndices && rightClip.phraseOriginalIndices
            ? rightClip.phraseOriginalIndices
            : undefined;

    const mergedTokens = sharedTokens
      ? leftClip.transcriptTokens
      : [...leftClip.transcriptTokens, ...rightClip.transcriptTokens];

    // Sibling-group preservation: the merge result keeps a sibling
    // id if EITHER half had one. This way merging one boundary
    // inside a multi-way split keeps the remaining splits visually
    // grouped. If neither side had an id, the merged clip stays
    // ungrouped (as it should — no sibling history).
    const mergedSiblingGroupId =
      leftClip.siblingGroupId ?? rightClip.siblingGroupId;

    const mergedClip = new KaraokeClip({
      id: leftClip.id,
      transcriptTokens: mergedTokens,
      hiddenTranscriptTokenIds: mergedHidden,
      ignoredByFiltersIds: mergedIgnored,
      karaokeConfig: leftClip.karaokeConfig,
      track: leftClip.track,
      translatedText: mergedText,
      translatedPhrases: mergedPhrases.length > 0 ? mergedPhrases : undefined,
      phraseOriginalIndices:
        mergedPhrases.length === 1 && sharedIdentity
          ? sharedIdentity
          : undefined,
      siblingGroupId: mergedSiblingGroupId,
      softBreakAfterTokenIds: [
        ...(leftClip.softBreakAfterTokenIds ?? []),
        ...(rightClip.softBreakAfterTokenIds ?? []),
      ],
      softBreakPhrasePositions: mergePhraseSoftBreaks(
        leftClip.softBreakPhrasePositions ?? [],
        rightClip.softBreakPhrasePositions ?? [],
        leftPhrases.length,
        inPhraseFusion ? { rightPhraseShift } : null,
      ),
      startSecondsOffset: leftClip.startSecondsOffset,
      endSecondsOffset: rightClip.endSecondsOffset,
    });

    track.karaokeClips = [
      ...track.karaokeClips.slice(0, rightIdx - 1),
      mergedClip,
      ...track.karaokeClips.slice(rightIdx + 1),
    ];

    // Drop the synthetic-break record that corresponds to THIS specific
    // boundary, not "the last record for the phrase". We identify the
    // record by the textPosition of the boundary being collapsed within
    // the reconstituted phrase. Only meaningful when the merge fused
    // two halves of the same source phrase (in-phrase synthetic split).
    // Across distinct phrases / natural boundaries no record exists.
    if (sharedIdentity && inPhraseFusion) {
      // The boundary text position equals the length of the LEFT clip's
      // translated text within the reconstituted phrase. Both halves
      // were trimmed when the break was applied, and the merged text
      // re-introduces the joining space, so the boundary position is
      // `leftClip.translatedText!.length` in the merged-text frame
      // (which matches how the break was recorded — see
      // `AddSyntheticCaptionBreakCommand`'s phrase-relative bookkeeping).
      const boundaryTextPosition = leftClip.translatedText?.length ?? 0;
      const matching = this.prevBreaks
        .map((b, idx) => ({ b, idx }))
        .filter(({ b }) =>
          sameOriginalIndices(b.phraseOriginalIndices, sharedIdentity),
        );
      if (matching.length > 0) {
        // Pick the break whose textPosition is closest to the boundary.
        // Floating-point reconstitution can shift positions by a few
        // characters when intermediate trims/joins differ; nearest-
        // match is robust to that without removing the wrong one when
        // multiple breaks exist on the same phrase.
        let bestIdx = matching[0].idx;
        let bestDelta = Math.abs(
          matching[0].b.textPosition - boundaryTextPosition,
        );
        for (let i = 1; i < matching.length; i++) {
          const d = Math.abs(matching[i].b.textPosition - boundaryTextPosition);
          if (d < bestDelta) {
            bestDelta = d;
            bestIdx = matching[i].idx;
          }
        }
        track.syntheticBreaks = [
          ...this.prevBreaks.slice(0, bestIdx),
          ...this.prevBreaks.slice(bestIdx + 1),
        ];
      }
    }

    this.editor.markDirty('caption_clips_merged');
    return true;
  }

  undo(): boolean {
    if (!this.prevClips || !this.prevBreaks) return false;
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;
    track.karaokeClips = this.prevClips;
    track.syntheticBreaks = this.prevBreaks;
    this.editor.markDirty('caption_clips_merged_undo');
    return true;
  }
}
